import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle2, ShieldQuestion, User, KeyRound } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import logo from '../assets/logo.png';
import CaptchaField, { captchaConfigured } from '../components/CaptchaField';

export default function ForgotPasswordPage() {
  const [mode, setMode] = useState('password'); // 'password' | 'username'
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);

  const isUsernameMode = mode === 'username';

  function switchMode(next) {
    setMode(next);
    setValue('');
    setError('');
    setCaptchaToken('');
    if (captchaRef.current) captchaRef.current.reset();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (captchaConfigured && !captchaToken) {
      setError('Por favor completa la verificación anti-bots.');
      return;
    }
    setError(''); setLoading(true);
    try {
      if (isUsernameMode) {
        await api.post('/auth/forgot-username', {
          email: value.trim(),
          captcha_token: captchaToken || undefined,
        });
      } else {
        await api.post('/auth/forgot-password', {
          username: value.trim(),
          captcha_token: captchaToken || undefined,
        });
      }
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setCaptchaToken('');
      if (captchaRef.current) captchaRef.current.reset();
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex flex-col" data-testid="forgot-password-page">
      <div className="p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 transition-colors">
          <ArrowLeft size={16} />Volver al inicio de sesión
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 -mt-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="CMIC" className="h-16 w-auto mb-4" />
            <h1 className="text-2xl font-bold text-charcoal-800">
              {isUsernameMode ? 'Recuperar usuario' : 'Recuperar contraseña'}
            </h1>
            <p className="text-sm text-charcoal-500 mt-1.5 text-center">
              {isUsernameMode
                ? 'Ingresa tu correo y te enviaremos tu nombre de usuario.'
                : 'Ingresa tu usuario o correo y te enviaremos un enlace para restablecer.'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            {sent ? (
              <div className="text-center py-6" data-testid="forgot-success">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h2 className="font-semibold text-charcoal-800 mb-2">Revisa tu correo</h2>
                <p className="text-sm text-charcoal-600 mb-6">
                  {isUsernameMode
                    ? 'Si el correo está registrado, recibirás un mensaje con tu nombre de usuario.'
                    : 'Si la cuenta existe, recibirás un correo con instrucciones para restablecer tu contraseña. El enlace expira en 1 hora.'}
                </p>
                <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-cmic-600 hover:text-cmic-700">
                  <ArrowLeft size={14} />Volver al inicio de sesión
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">
                    {isUsernameMode ? 'Correo electrónico' : 'Usuario o correo'}
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                    <input
                      type={isUsernameMode ? 'email' : 'text'}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      required
                      data-testid="forgot-input"
                      autoFocus
                      placeholder={isUsernameMode ? 'tu-correo@…' : 'tu-usuario o tu-correo@…'}
                      className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                    />
                  </div>
                </div>
                {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>}
                <CaptchaField ref={captchaRef} onToken={setCaptchaToken} className="" />
                <button type="submit" disabled={loading || !value.trim() || (captchaConfigured && !captchaToken)} data-testid="forgot-submit-btn" className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-200 disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Send size={16} />{isUsernameMode ? 'Enviar mi usuario' : 'Enviar enlace'}</>}
                </button>
                <div className="relative pt-2">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                    <span className="bg-white px-3 text-charcoal-400">o</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => switchMode(isUsernameMode ? 'password' : 'username')}
                  data-testid="forgot-toggle-mode-btn"
                  className="w-full flex items-center justify-center gap-2 bg-white border border-cmic-200 text-cmic-700 hover:bg-cmic-50 font-medium rounded-xl px-6 py-3 transition-colors text-sm"
                >
                  {isUsernameMode ? <><KeyRound size={16} />Olvidé mi contraseña</> : <><User size={16} />Olvidé mi usuario</>}
                </button>
                {!isUsernameMode && (
                  <Link
                    to="/recuperar-preguntas"
                    data-testid="forgot-security-questions-link"
                    className="w-full flex items-center justify-center gap-2 bg-white border border-cmic-200 text-cmic-700 hover:bg-cmic-50 font-medium rounded-xl px-6 py-3 transition-colors text-sm"
                  >
                    <ShieldQuestion size={16} />
                    Recuperar con preguntas de seguridad
                  </Link>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
